import { IConfig } from '../../config';
import { CurrencyExchangeRates } from '../../state';
import { CashInfo, CashInfoExtended, Currency, Inventory, SelectedAccountOrClient } from '../../types';
import { BaseCurrency, isDefined, Loadable, LoadableState, sumOfFieldInList } from '../../utils';
import { isNotRealTimeInventory } from './inventoryUtils';

/**
 * Calculate the value of the inventories with updated exchange rates
 */
const getInventoriesValue = ({
  inventoryList,
  accountOrClient,
  getCurrencyExchangeRateRealTime,
}: {
  inventoryList: LoadableState<Inventory[]>;
  accountOrClient: SelectedAccountOrClient;
  getCurrencyExchangeRateRealTime: (base: string, quote: string) => number;
}): LoadableState<number> => {
  return Loadable.map(inventoryList, (inventories): number => {
    const filteredInventories = isDefined(accountOrClient.account)
      ? inventories.filter(_ => _.accountNumber === accountOrClient.account!.accountNumber)
      : inventories.filter(_ => _.clientId === accountOrClient.client!);

    const result = filteredInventories.reduce((res, inventory) => {
      if (isNotRealTimeInventory(inventory)) {
        return res + inventory.grossProfitLossHuf;
      } else {
        const rate = getCurrencyExchangeRateRealTime(inventory.marketPriceCcy, BaseCurrency);

        return res + inventory.position * inventory.marketPriceSingle * rate;
      }
    }, 0);
    return result;
  });
};

/**
 * Gets a CashInfoExtended
 *
 * @param cashInfoByCurrency
 * @param currenciesExchangeRateRT
 * @param config
 */
const getCashInfoExtended = ({
  cashInfoByCurrency,
  currenciesExchangeRateRT,
  config,
}: {
  cashInfoByCurrency: Record<Currency, CashInfo>;
  currenciesExchangeRateRT: CurrencyExchangeRates;
  config: IConfig;
}): LoadableState<CashInfoExtended> => {
  const cashInfoByCurrencyUpdated = (Object.keys(cashInfoByCurrency) as Array<Currency>).map(currency => {
    if (currency === config.NetBroker.BaseCurrency) {
      return cashInfoByCurrency[currency];
    }
    const currencyExchangeRate = currenciesExchangeRateRT[`${currency}${BaseCurrency}=`];
    // have real time exchange rate
    if (currencyExchangeRate && currencyExchangeRate.isLoaded) {
      return {
        ...cashInfoByCurrency[currency],
        cashHuf: cashInfoByCurrency[currency].cash * currencyExchangeRate.rateInHuf,
        inventoryHuf: cashInfoByCurrency[currency].inventory * currencyExchangeRate.rateInHuf,
        allocatedHuf: cashInfoByCurrency[currency].allocated * currencyExchangeRate.rateInHuf,
      };
    }

    // can't find exchange rate yet
    return null;
  });

  if (cashInfoByCurrencyUpdated.findIndex(_ => _ === null) > -1) {
    return Loadable.loading;
  }

  const cashInfoListLoaded = cashInfoByCurrencyUpdated as Array<CashInfo>;

  return Loadable.loaded({
    cashInfo: cashInfoListLoaded,
    totalCashInHuf: sumOfFieldInList(cashInfoListLoaded, 'cashHuf'),
    inventoryInHuf: sumOfFieldInList(cashInfoListLoaded, 'inventoryHuf'),
    allocatedInHuf: sumOfFieldInList(cashInfoListLoaded, 'allocatedHuf'),
  });
};

export const BalanceUtils = {
  getInventoriesValue,
  getCashInfoExtended,
};
